"use client";

import { isEthereumWallet } from "@dynamic-labs/ethereum";
import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import { toast } from "@kyan/ui";
import { useMutation } from "@tanstack/react-query";
import { parseUnits } from "viem";

import { ONE_DAY_MS } from "#/config/time";
import { isMainnet } from "#/config/web3/contractAddresses";
import { tokenAddresses, tokens } from "#/config/web3/tokens";
import { useMintActivity } from "#/store/app/equityModal/mintActivity";

import { useChainId } from "./useChainId";
import { useUsdcBalance } from "./useUsdcBalance";
import { useWalletClient } from "./useWalletClient";

export const useMintUSDC = () => {
  const { chainId } = useChainId();
  const { refetch } = useUsdcBalance();
  const { walletClient } = useWalletClient();
  const { recordMint, lastMintedAt } = useMintActivity();
  const { primaryWallet } = useDynamicContext();

  const nextMintAvailableAt = lastMintedAt + ONE_DAY_MS;
  // eslint-disable-next-line react-hooks/purity -- Date.now() is used for a point-in-time availability check on each render
  const canMint = Date.now() >= nextMintAvailableAt;

  const isDisabled = !walletClient || isMainnet || !canMint;

  const { mutateAsync: mint, isPending: isLoading } = useMutation({
    mutationFn: async (amount: string) => {
      if (isDisabled || !walletClient.account) {
        throw new Error("Minting is currently unavailable.");
      }
      if (!primaryWallet || !isEthereumWallet(primaryWallet)) {
        throw new Error("Wallet account is not available.");
      }

      await primaryWallet.switchNetwork(chainId);

      return await walletClient.writeContract({
        account: walletClient.account,
        address: tokenAddresses.USDC[chainId],
        abi: [
          {
            type: "function",
            name: "mint",
            stateMutability: "nonpayable",
            inputs: [
              { name: "to", type: "address" },
              { name: "amount", type: "uint256" },
            ],
            outputs: [],
          },
        ],
        functionName: "mint",
        args: [
          walletClient.account.address,
          parseUnits(amount, tokens.USDC.decimals),
        ],
        chain: walletClient.chain,
      });
    },
    onSuccess: () => {
      refetch();
      toast.success({
        title: "Mint successful!",
        description: "Your mockUSDC balance has been updated.",
      });
      recordMint();
    },
    onError: (error) => {
      console.error(error);
      toast.error({
        title: "Mint failed",
        description: "There was an error minting mockUSDC. Please try again.",
      });
    },
  });

  return {
    mint,
    canMint,
    isDisabled,
    isLoading,
  };
};
